import { HTMLAttributes } from 'react';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs leading-none font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring/70',
  {
    variants: {
      variant: {
        // Every variant is bordered now (VALE-1725) — `border-transparent`
        // used to hide the base class's own `border`, so a `secondary`
        // Badge sat right next to an `outline` one rendering no edge at all
        // with no rule distinguishing when a call site got which. The base
        // `border` utility's default colour reads fine over any of these
        // fills; only the destructive/warning fills bump it to their own
        // tone for contrast, matching how `StatusBadge`'s tones already do.
        // VALE-1758 removed `warning`. It was the one variant reaching into
        // the SEMANTIC ramp (`bg-warning/15 text-warning-subtle-foreground`)
        // from the component that owns STRUCTURAL chips, which made it a
        // second definition of the amber chip alongside `StatusBadge`'s
        // `caution` tone — free to drift from it, and positioned to, since
        // nothing pointed the two at the same tokens. It had zero call sites,
        // so this deletes a drift hazard rather than a feature. Semantic state
        // is `StatusBadge` + a `tone`; `eslint-rules/no-semantic-colour-on-badge.js`
        // is what keeps it that way.
        default: 'border-primary/30 bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        destructive:
          'border-destructive/30 bg-destructive text-destructive-foreground',
        outline: 'text-foreground',
        'outline-solid': 'text-foreground'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /** optional leading Lucide icon; text-only is the default (LF-6146) */
  icon?: LucideIcon;
}

function Badge({
  className,
  variant,
  icon: Icon,
  children,
  ...props
}: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {Icon ? <Icon className="mr-1 h-3 w-3 shrink-0" aria-hidden /> : null}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
